// ===== BOOKSAGE REACT FRONTEND =====
// Main React component that provides the user interface for the AI book recommendation system

import React, { useState, useEffect } from 'react';
import './App.css';
import axios from 'axios'; // HTTP client for API calls

// Backend API configuration from environment variables
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  // ===== STATE MANAGEMENT =====
  const [problem, setProblem] = useState(''); // User's input problem/challenge
  const [recommendations, setRecommendations] = useState([]); // AI-generated book recommendations
  const [loading, setLoading] = useState(false); // Loading state for API calls
  const [popularProblems, setPopularProblems] = useState([]); // Suggested problems from analytics
  const [searchTime, setSearchTime] = useState(null); // Time taken for AI to generate recommendations
  const [sessionId, setSessionId] = useState(null); // Unique session ID for sharing/tracking
  const [showResults, setShowResults] = useState(false); // Toggle between search and results view
  const [error, setError] = useState(''); // Error messages
  const [dismissedProblems, setDismissedProblems] = useState([]); // Problems user has hidden

  useEffect(() => {
    // ===== APP INITIALIZATION =====
    // Runs once on app load to:
    // 1. Initialize sample books in database (if needed)
    // 2. Fetch popular problems for suggestions
    const initializeApp = async () => {
      try {
        await axios.post(`${API}/init-books`); // Ensure database has sample books
        const response = await axios.get(`${API}/popular-problems`); // Get trending searches
        setPopularProblems(response.data.problems);
      } catch (error) {
        console.error('Failed to initialize app:', error);
      }
    };
    initializeApp();
  }, []);

  // ===== CORE SEARCH FUNCTION =====
  // Sends user's problem to AI backend and receives book recommendations
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!problem.trim()) return;

    setLoading(true);
    setError('');
    setShowResults(false);

    try {
      // Call AI recommendation endpoint
      const response = await axios.post(`${API}/search`, {
        problem_text: problem
      }, {
        timeout: 20000 // 20 second timeout for AI processing
      });

      // Store AI recommendations and metadata
      setRecommendations(response.data.recommendations);
      setSearchTime(response.data.processing_time);
      setSessionId(response.data.session_id); // Save for social sharing
      setShowResults(true); // Switch to results view
    } catch (error) {
      console.error('Search failed:', error);
      setError(error.response?.data?.detail || 'Failed to get recommendations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleProblemClick = (problemText) => {
    setProblem(problemText);
    setShowResults(false);
  };

  const handleDismissProblem = (problemText, e) => {
    e.stopPropagation();
    setDismissedProblems([...dismissedProblems, problemText]);
  };

  // ===== SOCIAL SHARING FEATURE =====
  // Generates a beautiful infographic of recommendations for social media
  const downloadImage = async () => {
    if (!sessionId) {
      alert('No session found. Please search for books first.');
      return;
    }

    try {
      console.log('Generating infographic...');

      // Request backend to create infographic with recommendations
      const response = await axios.post(`${API}/generate-share-image`, null, {
        params: { session_id: sessionId },
        timeout: 15000 // 15 second timeout for image generation
      });

      const { image_base64, share_text } = response.data;
      console.log('Infographic generated successfully');

      // Trigger browser download of generated image
      const link = document.createElement('a');
      link.href = `data:image/png;base64,${image_base64}`;
      link.download = `booksage-${problem.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}.png`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Copy share text to clipboard for convenience
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(share_text);
        alert('📸 Infographic downloaded successfully!\\n\\n📋 Share text copied to clipboard - you can paste it when sharing on social media.');
      } else {
        // Older browser fallback
        const textArea = document.createElement('textarea');
        textArea.value = share_text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        alert('📸 Infographic downloaded successfully!\\n\\n📋 Share text copied to clipboard - you can paste it when sharing on social media.');
      }
      
    } catch (error) {
      console.error('Image generation failed:', error);
      alert('Unable to generate infographic at the moment. Please try again.');
    }
  };

  const resetSearch = () => {
    setShowResults(false);
    setProblem('');
    setRecommendations([]);
    setError('');
  };

  // ===== MAIN UI RENDER =====
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-cyan-50">
      {!showResults ? (
        // ===== SEARCH PAGE VIEW =====
        <div className="min-h-screen flex flex-col">
          {/* Hero Section - Centered Content */}
          <div className="flex-1 flex items-center justify-center px-4">
            <div className="w-full max-w-4xl">
              {/* Header */}
              <div className="text-center mb-12">
                <h1 className="text-6xl md:text-7xl font-bold text-gray-900 mb-6">
                  Book<span className="text-indigo-600">Sage</span>
                </h1>
                <p className="text-2xl md:text-3xl font-bold text-gray-800 max-w-3xl mx-auto leading-tight">
                  Transform how you discover business books.
                  <span className="block mt-2">Solve specific problems in 90 seconds, not browsing for 90 minutes.</span>
                </p>
              </div>

              {/* ===== SEARCH INPUT FORM ===== */}
              {/* Main interaction point where users enter their business problem */}
              <div className="mb-12">
            <form onSubmit={handleSearch} className="relative">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <input
                    type="text"
                    value={problem}
                    onChange={(e) => setProblem(e.target.value)}
                    placeholder="What's the business challenge you're facing? (e.g., 'client questioning our recommendations')"
                    className="w-full px-6 py-4 text-lg border border-gray-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent shadow-sm"
                    maxLength={500}
                    disabled={loading}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading || !problem.trim()}
                  className="px-8 py-4 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Searching...
                    </div>
                  ) : (
                    'Get Books'
                  )}
                </button>
              </div>
              {problem.length > 0 && (
                <div className="text-sm text-gray-500 mt-2">
                  {problem.length}/500 characters
                </div>
              )}
            </form>

                {error && (
                  <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Section - Popular Problems and Footer */}
          <div className="pb-8">
            {/* ===== POPULAR PROBLEMS SUGGESTIONS ===== */}
            {/* Shows trending searches to inspire users and provide quick access */}
            {popularProblems.length > 0 && (
              <div className="max-w-4xl mx-auto px-4 mb-12">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6 text-center">
                  Popular Business Challenges
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {popularProblems
                    .filter(problem => problem && problem.trim() && !dismissedProblems.includes(problem))
                    .slice(0, 9)
                    .map((popularProblem, index) => (
                    <div
                      key={index}
                      className="relative p-4 bg-white border border-gray-200 rounded-lg hover:border-indigo-300 hover:shadow-md transition-all duration-200 group cursor-pointer"
                      onClick={() => handleProblemClick(popularProblem)}
                    >
                      <button
                        onClick={(e) => handleDismissProblem(popularProblem, e)}
                        className="absolute top-2 right-2 w-6 h-6 text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                      <div className="text-gray-800 group-hover:text-indigo-600 transition-colors pr-8">
                        {popularProblem}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="text-center text-gray-500">
              <p className="mb-4">Powered by Impleo AI • Business books curated for professionals</p>
              <div className="flex justify-center items-center space-x-4 text-sm">
                <a href="/privacy" className="hover:text-indigo-600 transition-colors">
                  Privacy
                </a>
                <span className="text-gray-400">|</span>
                <a href="/terms" className="hover:text-indigo-600 transition-colors">
                  Terms of Use
                </a>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* ===== RESULTS PAGE VIEW ===== */
        /* Displays AI-generated book recommendations with insights */
        <div className="container mx-auto px-4 py-8">
          {/* Header with Back Button */}
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={resetSearch}
              className="flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              New Search
            </button>
            
            <div className="text-center flex-1">
              <h1 className="text-2xl font-bold text-gray-900">
                Book<span className="text-indigo-600">Sage</span>
              </h1>
            </div>
            
            <button
              onClick={downloadImage}
              className="flex items-center text-indigo-600 hover:text-indigo-700 font-medium"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Download
            </button>
          </div>

          {/* Problem & Results Info */}
          <div className="bg-white rounded-xl p-6 mb-8 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-2">Your Challenge:</h2>
            <p className="text-gray-700 mb-4 text-lg">"{problem}"</p>
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>3 recommendations found</span>
              {searchTime && (
                <span>Generated in {searchTime.toFixed(2)}s</span>
              )}
            </div>
          </div>

          {/* ===== AI BOOK RECOMMENDATIONS DISPLAY ===== */}
          {/* Each card shows: book info, 3 takeaways, golden nugget, relevance */}
          <div className="space-y-8">
            {recommendations.map((book, index) => (
              <div key={book.id} className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
                <div className="p-8">
                  <div className="flex flex-col lg:flex-row gap-6">
                    {/* Book Info */}
                    <div className="lg:w-1/3">
                      <div className="flex items-start gap-4">
                        <div className="w-20 h-28 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-md flex items-center justify-center flex-shrink-0">
                          <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h3 className="text-xl font-bold text-gray-900 mb-1">{book.title}</h3>
                          <p className="text-gray-600 mb-2">by {book.author}</p>
                          <div className="flex items-center gap-2 mb-3">
                            <span className="px-3 py-1 bg-indigo-100 text-indigo-700 text-sm font-medium rounded-full">
                              #{index + 1}
                            </span>
                            <span className="px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
                              {book.time_to_impact}
                            </span>
                          </div>
                          {book.amazon_url && (
                            <a
                              href={book.amazon_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center text-indigo-600 hover:text-indigo-700 font-medium text-sm"
                            >
                              View on Amazon
                              <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                              </svg>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Takeaways & Insights */}
                    <div className="lg:w-2/3">
                      {/* Why Relevant */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2">Why This Book Helps</h4>
                        <p className="text-gray-700 leading-relaxed">{book.why_relevant}</p>
                      </div>

                      {/* Three Takeaways */}
                      <div className="mb-6">
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Key Takeaways</h4>
                        <div className="space-y-3">
                          {[book.takeaway_1, book.takeaway_2, book.takeaway_3,book.actionable_steps].map((takeaway, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-semibold mt-0.5">
                                {idx + 1}
                              </span>
                              <p className="text-gray-700 leading-relaxed">{takeaway}</p>
                            </div>
                          ))}
                        </div>
                        <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">Actionable Steps</h4>
                        <div className="space-y-3">
                          {[book.actionable_steps].map((takeaway, idx) => (
                            <div key={idx} className="flex items-start gap-3">
                              <span className="flex-shrink-0 w-6 h-6 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-semibold mt-0.5">
                                {idx + 1}
                              </span>
                              <p className="text-gray-700 leading-relaxed">{takeaway}</p>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* ===== GOLDEN NUGGET ===== */}
                      {/* Highlights the most valuable framework/technique from the book */}
                      <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-200 rounded-lg p-4">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <div>
                            <h4 className="font-semibold text-yellow-800 mb-1">Golden Nugget</h4>
                            <p className="text-yellow-700 leading-relaxed">{book.golden_nugget}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA Section */}
          <div className="text-center mt-12 py-8">
            <button
              onClick={resetSearch}
              className="px-8 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Search Another Problem
            </button>
          </div>

          {/* Footer */}
          <div className="text-center mt-8 pb-8 text-gray-500">
            <p className="mb-4">Powered by AI • Business books curated for professionals</p>
            <div className="flex justify-center items-center space-x-4 text-sm">
              <a href="/privacy" className="hover:text-indigo-600 transition-colors">
                Privacy
              </a>
              <span className="text-gray-400">|</span>
              <a href="/terms" className="hover:text-indigo-600 transition-colors">
                Terms of Use
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;